/**
 * sheet-cache.ts —— CSSStyleSheet 缓存与全局变量注入
 *
 * 为什么要有这个模块：
 *
 * CSS 变量能穿透 shadow DOM，但前提是变量定义在**宿主文档**上。
 * 组件内部拿到的只是变量名，值仍然是全局的 —— 这正是我们要的：
 * 主题、字号只在一处改，所有 shadow root 同步生效。
 *
 * 所以这里做两件事：
 *   1. tokens.css / theme-*.css / base.css 注入到 document（全局宿主）
 *   2. 组件自己的样式用 CSSStyleSheet 缓存，同类组件共享一份，避免重复解析
 */

import tokensCss from '@/styles/tokens.css?inline';
import themeLightCss from '@/styles/theme-light.css?inline';
import themeDarkCss from '@/styles/theme-dark.css?inline';
import baseCss from '@/styles/base.css?inline';

/** 组件样式缓存：tagName → CSSStyleSheet */
class SheetCache {
  private cache = new Map<string, CSSStyleSheet>();

  get(key: string, factory: () => string): CSSStyleSheet {
    let sheet = this.cache.get(key);
    if (!sheet) {
      sheet = new CSSStyleSheet();
      sheet.replaceSync(factory());
      this.cache.set(key, sheet);
    }
    return sheet;
  }
}

export const sheetCache = new SheetCache();

/** 把设计变量与基础样式注入到宿主文档。只执行一次 */
let injected = false;
export function injectGlobalStyles(): void {
  if (injected) return;
  injected = true;

  const docSheet = new CSSStyleSheet();
  // 顺序很重要：先变量，再主题，最后 reset —— 后者可覆盖前者
  docSheet.replaceSync(`${tokensCss}\n${themeLightCss}\n${themeDarkCss}\n${baseCss}`);
  document.adoptedStyleSheets = [...document.adoptedStyleSheets, docSheet];
}

/**
 * 每个 shadow root 共享的「基础重置」样式表。
 *
 * 为什么必须存在：
 *   html/body 上的 `* { box-sizing: border-box }` 只在宿主文档里匹配，
 *   影子树内的元素**不会**继承这条规则 —— box-sizing 不继承，
 *   元素选择器（`button`、`h2`、`svg`…）也不会穿透 shadow 边界。
 *   结果是组件写了 width:100% + padding 就按 content-box 算，
 *   实际宽度超出父容器，凭空产生横向滚动条；按钮还会带上浏览器默认边框和留白。
 *   所以每个 shadow root 都要自己声明一份最小重置。
 */
const BASE_RESET_CSS = `
  :host,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  h1, h2, h3, h4, h5, h6, p, figure, blockquote, dl, dd {
    margin: 0;
  }

  ul, ol {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  button {
    font: inherit;
    color: inherit;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  input, textarea, select {
    font: inherit;
    color: inherit;
  }

  img, video, canvas, svg {
    display: block;
    max-width: 100%;
  }

  :focus-visible {
    outline: 2px solid var(--c-accent);
    outline-offset: 2px;
  }

  /* 滚动条样式同样不穿透 shadow 边界，需要每个 shadow root 各自声明 */
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  ::-webkit-scrollbar-thumb {
    background: var(--c-border-strong);
    border-radius: var(--radius-full);
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }
`;

/** 基类注入的共享样式表（基础重置）。同一实例被所有 shadow root 复用 */
export function baseResetSheet(): CSSStyleSheet {
  return sheetCache.get('__base-reset', () => BASE_RESET_CSS);
}
